import { useState, useEffect, useCallback, useRef } from 'react'
import { tagService } from '@/services/TagService'
import type { Tag } from '@/services/TagService'
import { toast } from 'sonner'

interface CreateTagPayload {
  name: string;
  color: string;
  description?: string;
  tag_type: 'user' | 'conversation' | 'system' | 'document' | 'property' | 'universal';
}

interface UseTagsOptions {
  tagType?: string
  autoLoad?: boolean
  search?: string
  orderBy?: 'name' | 'color' | 'created_at' | 'updated_at'
  order?: 'asc' | 'desc'
}

export function useTags(options: UseTagsOptions = {}) {
  const {
    tagType,
    autoLoad = true,
    search = '',
    orderBy = 'name',
    order = 'asc'
  } = options

  const [tags, setTags] = useState<Tag[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const loadRequestIdRef = useRef(0)

  const loadTags = useCallback(async (overrides?: {
    tagType?: string
    search?: string
    orderBy?: 'name' | 'color' | 'created_at' | 'updated_at'
    order?: 'asc' | 'desc'
  }) => {
    const requestId = ++loadRequestIdRef.current
    setLoading(true)
    setError(null)

    try {
      const response = await tagService.getTags({
        type: overrides?.tagType ?? tagType,
        search: overrides?.search ?? search,
        orderBy: overrides?.orderBy ?? orderBy,
        order: overrides?.order ?? order,
      })
      if (requestId === loadRequestIdRef.current) {
        setTags(response)
      }
    } catch (err) {
      if (requestId === loadRequestIdRef.current) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load tags'
        setError(errorMessage)
        toast.error(errorMessage)
      }
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false)
      }
    }
  }, [tagType, search, orderBy, order])

  const createTag = useCallback(async (payload: CreateTagPayload) => {
    setLoading(true)
    setError(null)
    
    try {
      const newTag = await tagService.createTag(payload)
      if (newTag && newTag.id) {
        setTags(prev => [...(prev || []), newTag])
        toast.success(`Tag "${newTag.name || 'Unknown'}" created successfully`)
        return newTag
      } else {
        throw new Error(`Invalid tag response from server: ${JSON.stringify(newTag)}`)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create tag'
      setError(errorMessage)
      toast.error(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const updateTag = useCallback(async (id: string, payload: { name?: string; color?: string; description?: string }) => {
    setLoading(true)
    setError(null)
    
    try {
      const updatedTag = await tagService.updateTag(id, payload)
      if (updatedTag && updatedTag.id) {
        setTags(prev => (prev || []).map(tag => tag.id === id ? updatedTag : tag))
        toast.success(`Tag "${updatedTag.name || 'Unknown'}" updated successfully`)
        return updatedTag
      } else {
        throw new Error('Invalid tag response from server')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update tag'
      setError(errorMessage)
      toast.error(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const deleteTag = useCallback(async (id: string) => {
    setLoading(true)
    setError(null)
    
    try {
      await tagService.deleteTag(id)
      setTags(prev => (prev || []).filter(tag => tag.id !== id))
      toast.success('Tag deleted successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete tag'
      setError(errorMessage)
      toast.error(errorMessage)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (autoLoad) {
      loadTags()
    }
  }, [loadTags, autoLoad])

  return {
    tags,
    loading,
    error,
    loadTags,
    createTag,
    updateTag,
    deleteTag,
    refresh: loadTags
  }
}

// User tags functionality removed as it's not available in the current TagService
